import { Box } from '@chakra-ui/react';
import { useOverviewContext } from '@resistapp/client/contexts/use-overview-context/use-overview-context';
import { OverviewDatum } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import {
  AllProjectSampleTypesGroup,
  ComparableSampleGroupType,
  SampleTypeGroup,
  SmplPntGroup,
  comparableGroupSpecs,
  getFriendlyComparableSampleGroupName,
} from '@resistapp/common/comparable-sample-groups';
import { TestSelectors } from '@resistapp/common/testing/visual-test-selectors';
import { MetricMode } from '@resistapp/common/types';
import { IntegratedTextSelect } from '../selects/integrated-text-select';
import { theme } from '../shared/theme';
import { OptionForSampleType } from './types';

interface Props {
  onChange: (value: OptionForSampleType['value'] | undefined) => void;
  active: boolean;
  setActive: (value: boolean) => void;
}

export function SampleTypeSelector({ onChange, active, setActive }: Props) {
  const { availableSmplPntGroups, selectedSampleTypeGroup, metricMode, selectedSiteDatum } = useOverviewContext();

  if (!availableSmplPntGroups) return null;

  const groupsWithAllSamplesFirst: SmplPntGroup[] =
    availableSmplPntGroups.length > 1
      ? [
          availableSmplPntGroups[availableSmplPntGroups.length - 1],
          ...availableSmplPntGroups.slice(0, availableSmplPntGroups.length - 1),
        ]
      : availableSmplPntGroups;

  const options: OptionForSampleType[] = groupsWithAllSamplesFirst.map(({ type }) => {
    return {
      value: type,
      label: getFriendlyComparableSampleGroupName(type),
      disabled: isSelectionDisabled(type, metricMode, selectedSiteDatum),
    };
  });

  // Find the selected option or use the first available option
  const value =
    options.length > 0 ? options.find(option => option.value === selectedSampleTypeGroup) || options[0] : undefined;

  return (
    <Box
      onClick={() => {
        !active && setActive(true);
      }}
      style={{ cursor: 'pointer' }}
    >
      <IntegratedTextSelect<OptionForSampleType>
        className={TestSelectors.TEST_SAMPLE_TYPE_SELECTOR}
        options={options}
        value={value}
        size="sm"
        menuIsOpen={active}
        onChange={selectedOption => {
          onChange(selectedOption?.value);
          setActive(false);
        }}
        onMenuClose={() => {
          setActive(false);
        }}
        renderOption={option => <div>{option.label}</div>}
      >
        <div
          style={{
            fontWeight: theme.fontWeight.bold,
          }}
        >
          {value?.label || 'Select sampling point type'}
        </div>
      </IntegratedTextSelect>
    </Box>
  );
}

function isSelectionDisabled(
  type: SampleTypeGroup,
  metricMode: MetricMode,
  selectedSiteDatum: OverviewDatum | undefined,
) {
  const reductionModeAndSiteWithoutProcessData =
    metricMode === MetricMode.REDUCTION &&
    !!selectedSiteDatum &&
    (!selectedSiteDatum.samplingPointAfter ||
      !comparableGroupSpecs[ComparableSampleGroupType.WATER_TREATMENT].smplPntFilterer(
        selectedSiteDatum.samplingPointAfter,
      ) ||
      !comparableGroupSpecs[ComparableSampleGroupType.WATER_TREATMENT].smplPntFilterer(
        selectedSiteDatum.samplingPoint,
      ));
  const otherThanAllSamples = type !== AllProjectSampleTypesGroup.ALL_PROJECT_SAMPLING_POINTS;
  const waterTreatmentIffReduction =
    (metricMode === MetricMode.REDUCTION) !== (type === ComparableSampleGroupType.WATER_TREATMENT);
  const siteWithoutDataForType =
    !!selectedSiteDatum &&
    !comparableGroupSpecs[type].smplPntFilterer(selectedSiteDatum.samplingPoint) &&
    (!selectedSiteDatum.samplingPointAfter ||
      !comparableGroupSpecs[type].smplPntFilterer(selectedSiteDatum.samplingPointAfter));
  return (
    reductionModeAndSiteWithoutProcessData || // Disable everything in reduction mode on sites without process data
    (otherThanAllSamples && // Otherwise always enable 'All samples'
      (waterTreatmentIffReduction || // Otherwise disable water treatment for risk&argi (and all others for reduction)
        siteWithoutDataForType)) // Disable selections that have no available data on site view
  );
}
